import React, { useState, useCallback, useEffect, useMemo } from "react";
import "./ProfileInfo.css";
import profilePic from "../../assets/images/profile_image.png";
import Button from "../../components/Button/Button3";
import axios from "axios";
import Cropper from 'react-easy-crop';
import getCroppedImg from '../../utils/getCroppedImg';
import withCacheBust from "../../utils/withCacheBust";
import compressImageToMaxSize from "../../utils/compressImageToMaxSize";
import formatCountLabel from "../../utils/formatCountLabel";
import { SUBSCRIPTIONS_ENABLED } from "../../utils/features";
import EditProfile from "../EditProfile/EditProfile";
import { useAlertDialog } from "../ConfirmDialog/ConfirmDialogProvider";
import { formatProfileDisplayName } from "../../utils/profileDisplayName";


const ProfileInfo = ({
  userData,
  onProfilePictureUpdate,
  onProfileDataUpdate,
  profilePictureVersion,
  onEditModeChange,
  readOnly = false,
  hideSensitiveFields = false,
}) => {
  const isMobile = window.innerWidth < 768;
  const aspectValue = useMemo(() => (isMobile ? 1 : (459.102 / 428.719)), [isMobile]);
  const cropSizeValue = useMemo(
    () => (isMobile ? { width: 200, height: 200 } : { width: 459.102, height: 428.719 }),
    [isMobile]
  );
  const isReadOnly = Boolean(readOnly);
  const shouldHideSensitiveFields = Boolean(hideSensitiveFields || isReadOnly);
  const displayName = formatProfileDisplayName(userData, { readOnly: isReadOnly });
  const compactNameLength = displayName.replace(/\s+/g, "").length;
  const firstNameClassName = [
    "first-name",
    compactNameLength >= 16
      ? "first-name--very-long"
      : compactNameLength >= 12
        ? "first-name--long"
        : "",
  ]
    .filter(Boolean)
    .join(" ");

  const [profileImageUrl, setProfileImageUrl] = useState(userData.profilePictureUrl || profilePic);
  const [selectedFile, setSelectedFile] = useState(null);

  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [minZoom, setMinZoom] = useState(1);

  const [isEditing, setIsEditing] = useState(false);
  const showAlert = useAlertDialog();

  useEffect(() => {
    if (!userData) return;
    const resolvedUrl = userData.profilePictureUrl
      ? withCacheBust(userData.profilePictureUrl, profilePictureVersion)
      : profilePic;
    setProfileImageUrl(resolvedUrl);
  }, [profilePictureVersion, userData]);

  useEffect(() => {
    if (typeof onEditModeChange === "function") {
      onEditModeChange(isEditing);
    }

    return () => {
      if (typeof onEditModeChange === "function") {
        onEditModeChange(false);
      }
    };
  }, [isEditing, onEditModeChange]);

  const handleEditSuccess = (updatedUser) => {
    setIsEditing(false);
    if (updatedUser && typeof onProfileDataUpdate === "function") {
      onProfileDataUpdate(updatedUser);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];

      if (!allowedTypes.includes(file.type)) {
        showAlert({
          title: "PROFILE PICTURE",
          message: "Only JPEG, PNG, and GIF files are allowed.",
        });
        return;
      }

      // If original file is large, we still allow it because we'll compress after cropping.

      const reader = new FileReader();
      reader.onloadend = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setMinZoom(1);
        setCropImageSrc(reader.result);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
      setSelectedFile(file);
    }
  };

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onMediaLoaded = useCallback(
    (mediaSize) => {
      const mediaW = mediaSize.naturalWidth || mediaSize.width;
      const mediaH = mediaSize.naturalHeight || mediaSize.height;
      if (!mediaW || !mediaH) return;

      const minZoomValue = Math.max(
        cropSizeValue.width / mediaW,
        cropSizeValue.height / mediaH,
        1
      );

      setMinZoom(minZoomValue);
      setZoom((current) => (current < minZoomValue ? minZoomValue : current));
    },
    [cropSizeValue]
  );
  
  const handleConfirmCrop = async () => {
    try {
      if (!cropImageSrc || !croppedAreaPixels) {
        await showAlert({
          title: "PROFILE PICTURE",
          message: "Image or crop area not set correctly.",
        });
        return;
      }
  
      const croppedBlob = await getCroppedImg(cropImageSrc, croppedAreaPixels);
      const maxSizeInBytes = 5 * 1024 * 1024;
      const finalBlob = await compressImageToMaxSize(croppedBlob, maxSizeInBytes);
      const croppedUrl = URL.createObjectURL(finalBlob);
      setProfileImageUrl(croppedUrl);

      const fileNameBase = selectedFile?.name
        ? selectedFile.name.replace(/\.[^.]+$/, "")
        : "profile";
      const croppedFile = new File([finalBlob], `${fileNameBase}.jpg`, { type: "image/jpeg" });
      handleImageUpload(croppedFile);

      setShowCropModal(false);
    } catch (error) {
      console.error("Error cropping image:", error);
      await showAlert({
        title: "PROFILE PICTURE",
        message: "Failed to crop the image. Please try again.",
      });
    }
  };

  const handleImageUpload = async (file) => {
    const idToken = localStorage.getItem("idToken");
    if (!idToken) {
      await showAlert({
        title: "PROFILE PICTURE",
        message: "You must be logged in to perform this action.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("profilePicture", file);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/profile/upload-profile-picture`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (response.data.profilePictureUrl) {
        const rawUrl = response.data.profilePictureUrl;
        const version = Date.now();
        const uniqueUrl = withCacheBust(rawUrl, version);
        setProfileImageUrl(uniqueUrl);
        if (onProfilePictureUpdate) onProfilePictureUpdate(rawUrl, version);
        await showAlert({
          title: "PROFILE PICTURE",
          message: "Profile picture uploaded successfully!",
        });
      }
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      await showAlert({
        title: "PROFILE PICTURE",
        message: "Failed to upload profile picture. Please try again.",
      });
    }
  };

  // Compute the total credits from userData.credits array
  const totalCredits = Array.isArray(userData.credits)
    ? userData.credits.reduce((sum, entry) => sum + (entry.amount || 0), 0)
    : 0;

  return (
    <div className="profile-info-container">
      <div className="profile-info">
        <div className="profile-image-wrapper">
          <div className={`profile-image${isReadOnly ? " profile-image--readonly" : ""}`}>
            {isReadOnly ? (
              <img src={profileImageUrl} alt={`${displayName || "Player"}'s profile`} />
            ) : (
              <>
                <label htmlFor="profilePictureInput" className="image-label">
                  <img src={profileImageUrl} alt={`${userData.first_name}'s profile`} />
                </label>
                <span className="profile-image-caption">Update Picture</span>
                <input
                  type="file"
                  id="profilePictureInput"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />
              </>
            )}
          </div>
        </div>
        
        <div className="profile-details">
          <p className={firstNameClassName}>{displayName}</p>
          {isReadOnly && userData?.position && userData.position !== "Unknown" && (
            <p>{userData.position}</p>
          )}
          {isReadOnly && userData?.rank != null && (
            <p className="profile-credits">{`RANKING: ${userData.rank}`}</p>
          )}
          {!shouldHideSensitiveFields && <p>{userData.email}</p>}
          {!shouldHideSensitiveFields && <p>{userData.phone_number}</p>}
          {/* Credits are bought through subscriptions, which are switched off,
              so a balance would only ever read 0. Shown again when they are. */}
          {!shouldHideSensitiveFields && SUBSCRIPTIONS_ENABLED && (
            <p className="profile-credits">{formatCountLabel(totalCredits, "CREDIT", "CREDITS")}</p>
          )}
          {!isReadOnly && (
            <Button
              variant="small"
              primaryText="Edit"
              styleType="default"
              onClick={() => setIsEditing(true)}
            />
          )}
        </div>
      </div>


      {!isReadOnly && showCropModal && (
        <div className="crop-modal">
          <div className="crop-editor">
            <div className="crop-container">
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                minZoom={minZoom}
                onMediaLoaded={onMediaLoaded}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                showGrid={false}
                restrictPosition
                aspect={aspectValue}
                cropSize={cropSizeValue}
                cropShape="rect"
                mediaProps={{ style: { objectFit: 'cover' } }}
                style={{
                  containerStyle: {
                    backgroundColor: '#ffff',
                    width: '100%',
                    height: '100%',
                    position: 'relative'
                  },
                  mediaStyle: {
                    background: 'transparent'
                  },
                  cropAreaStyle: {
                    background: 'transparent',
                    border: 'none'
                  },
                }}
              />
            </div>

            <div className="zoom-slider-container" aria-label="Zoom control">
              <span className="zoom-label zoom-label-plus">+</span>
              <input
                type="range"
                min="1"
                max="3"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="zoom-slider"
              />
              <span className="zoom-label zoom-label-minus">-</span>
            </div>
          </div>

          <div className="crop-controls">
            <Button
              variant="small"
              primaryText="Confirm Crop"
              onClick={handleConfirmCrop}
              styleType="default"
            />
            <Button
              variant="small"
              primaryText="Cancel"
              onClick={() => setShowCropModal(false)}
              styleType="default"
            />
          </div>
        </div>
      )}

      {!isReadOnly && isEditing && <EditProfile onEditSuccess={handleEditSuccess} />}

      <div className="noiseeffect"></div>
    </div>
  );
};

export default ProfileInfo;
